/**
 * getSpeechHistory envelope 列表 → Web 观众多列字幕行（mixed-era 兼容 legacy flat）
 */

import { isEnvelopeData, shortLangCode } from '../adapters/live-speech-adapter'
import type { LiveSpeechEnvelopeData } from './build-live-speech-envelope'
import type { SpeechHistoryEnvelopeEntry, ViewerSubtitleRow } from '../types'

export interface SpeechHistoryFlatItem {
  beginTime?: number
  endTime?: number
  content?: string
  languageCode?: string
  contentSource?: string
  speeker?: string
  speaker?: string
  duration?: number
}

/** 同一 utterance 的 envelope 时间轴 + 说话人 */
export function utteranceHistoryKey(item: {
  beginTime?: number
  endTime?: number
  speeker?: string
  speaker?: string
  data?: LiveSpeechEnvelopeData
}): string {
  const begin = item.beginTime ?? item.data?.beginTime ?? 0
  const end = item.endTime ?? item.data?.endTime ?? 0
  const speaker = item.speeker || item.speaker || item.data?.speeker || ''
  return `${begin}:${end}:${speaker}`
}

function isLegacyFlatItem(item: SpeechHistoryFlatItem): boolean {
  return Boolean(item.contentSource || item.content)
}

function envelopeToViewerRow(
  envelope: LiveSpeechEnvelopeData,
  outerBegin?: number,
  outerEnd?: number,
): ViewerSubtitleRow | null {
  if ((envelope.duration ?? 0) < 1) {
    return null
  }
  const translations: Record<string, string> = {}
  for (const cell of envelope.listenText ?? []) {
    if (cell.text?.trim()) {
      translations[shortLangCode(cell.languageCode)] = cell.text
    }
  }
  if (envelope.readText?.text?.trim()) {
    translations[shortLangCode(envelope.readText.languageCode)] = envelope.readText.text
  }
  if (!envelope.readText?.text?.trim() && !Object.keys(translations).length) {
    return null
  }
  return {
    source_text: envelope.readText?.text ?? '',
    translations,
    speaker: envelope.speeker ?? '',
    ts: String(outerEnd ?? envelope.endTime ?? outerBegin ?? envelope.beginTime ?? Date.now()),
  }
}

function mergeEnvelopeHistoryEntries(entries: unknown[]): ViewerSubtitleRow[] {
  const byUtterance = new Map<string, ViewerSubtitleRow>()
  for (const raw of entries) {
    if (!raw || typeof raw !== 'object') {
      continue
    }
    const entry = raw as SpeechHistoryEnvelopeEntry & SpeechHistoryFlatItem
    if (isLegacyFlatItem(entry)) {
      continue
    }
    const envelope = isEnvelopeData(entry.data)
      ? entry.data
      : isEnvelopeData(entry)
        ? (entry as unknown as LiveSpeechEnvelopeData)
        : null
    if (!envelope) {
      continue
    }
    const key = utteranceHistoryKey({
      beginTime: entry.beginTime,
      endTime: entry.endTime,
      data: envelope,
    })
    if (byUtterance.has(key)) {
      continue
    }
    const row = envelopeToViewerRow(envelope, entry.beginTime, entry.endTime)
    if (row) {
      byUtterance.set(key, row)
    }
  }
  return Array.from(byUtterance.values()).sort((a, b) => {
    const ta = Number(a.ts || 0)
    const tb = Number(b.ts || 0)
    return ta - tb
  })
}

/**
 * 将 history 合并为带 translations 的字幕行。
 * - envelope era：raw 为 getSpeechHistoryResult.data（全量 envelope 列表）
 * - legacy era：byLanguage 为各语种扁平行列表
 */
export function mergeSpeechHistoryByUtterance(
  historyOrByLanguage: unknown[] | Map<string, SpeechHistoryFlatItem[]>,
  targetLanguageCodes: string[],
): ViewerSubtitleRow[] {
  if (Array.isArray(historyOrByLanguage)) {
    return mergeEnvelopeHistoryEntries(historyOrByLanguage)
  }

  const byLanguage = historyOrByLanguage
  const byUtterance = new Map<string, ViewerSubtitleRow>()

  for (const langCode of targetLanguageCodes) {
    const short = shortLangCode(langCode)
    const items =
      byLanguage.get(short)
      ?? byLanguage.get(langCode)
      ?? []
    for (const item of items) {
      if ((item.duration ?? 0) <= 0) {
        continue
      }
      const key = utteranceHistoryKey(item)
      let row = byUtterance.get(key)
      if (!row) {
        row = {
          source_text: '',
          translations: {},
          speaker: item.speeker || item.speaker || '',
          ts: String(item.endTime ?? item.beginTime ?? Date.now()),
        }
        byUtterance.set(key, row)
      }
      const itemShort = shortLangCode(item.languageCode || langCode)
      const text = (item.content || '').trim()
      if (!text) {
        continue
      }
      if (item.contentSource === 'voice') {
        row.source_text = text
      }
      row.translations[itemShort] = text
    }
  }

  return Array.from(byUtterance.values()).sort((a, b) => {
    const ta = Number(a.ts || 0)
    const tb = Number(b.ts || 0)
    return ta - tb
  })
}
